import { Platform } from '../../../types/Platforms';

// The 5 video-only platforms
export const VIDEO_PLATFORMS: Platform[] = [
  Platform.INSTAGRAM_REELS,
  Platform.YOUTUBE_SHORTS,
  Platform.TIKTOK_REELS,
  Platform.FACEBOOK_REELS,
  Platform.LINKEDIN_REELS,
];

// Represents any video that can be saved to platforms
export interface VideoForPlatform {
  videoUrl: string;
  thumbnailUrl?: string;
  title?: string;
  sourceType: 'ugc' | 'upload' | 'history';
  mediaId?: string;
  /** Script/transcript used for AI-generated descriptions (e.g. from UGC video) */
  script?: string;
  /** Pre-generated caption from agent (when available, used instead of AI generating from script) */
  generatedCaption?: string;
}

// Per-platform editable description
export interface PlatformDescription {
  platform: Platform;
  title: string;
  content: string;
  hashtags: string[];
  callToAction: string;
  postId?: string;
}

// Flow states for SaveToPlatformPanel
export type SaveToPlatformStep =
  | 'select_platforms'
  | 'generating_descriptions'
  | 'edit_descriptions'
  | 'saving'
  | 'success';

// Video history item from GET /videos/list?customer_id={id}
export interface VideoHistoryItem {
  media_id: string;
  cloudfront_url: string;
  s3_key: string;
  created_at: string;
  transcript?: string;
  avatar_id?: string;
  voice_id?: string;
}

// Response from GET /videos/list?customer_id={id}
export interface FetchVideosResponse {
  videos: VideoHistoryItem[];
  count: number;
  next_key: string | null;
}

// Platform display helpers
export const getPlatformLabel = (platform: Platform): string => {
  const labels: Record<string, string> = {
    [Platform.INSTAGRAM_REELS]: 'Instagram Reels',
    [Platform.YOUTUBE_SHORTS]: 'YouTube Shorts',
    [Platform.TIKTOK_REELS]: 'TikTok',
    [Platform.FACEBOOK_REELS]: 'Facebook Reels',
    [Platform.LINKEDIN_REELS]: 'LinkedIn Reels',
  };
  return labels[platform] || platform.replace(/_/g, ' ');
};

export const getPlatformColor = (platform: Platform): string => {
  const colors: Record<string, string> = {
    [Platform.INSTAGRAM_REELS]: 'bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af]',
    [Platform.YOUTUBE_SHORTS]: 'bg-[#ff0000]',
    [Platform.TIKTOK_REELS]: 'bg-[#000000]',
    [Platform.FACEBOOK_REELS]: 'bg-[#1877f2]',
    [Platform.LINKEDIN_REELS]: 'bg-[#0077b5]',
  };
  return colors[platform] || 'bg-[#2f4941]';
};

export const getPlatformIcon = (platform: Platform): string => {
  const icons: Record<string, string> = {
    [Platform.INSTAGRAM_REELS]: 'photo_camera',
    [Platform.YOUTUBE_SHORTS]: 'videocam',
    [Platform.TIKTOK_REELS]: 'music_note',
    [Platform.FACEBOOK_REELS]: 'facebook',
    [Platform.LINKEDIN_REELS]: 'business',
  };
  return icons[platform] || 'campaign';
};
